import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type FinalePhase = 'horizon' | 'flagship' | 'fleet' | 'ecosystem';

interface FinaleOptions { reducedMotion: boolean; }
interface VideoScrubber { load(): void; setProgress(progress: number): void; pause(): void; }

const clamp = (value: number): number => Math.min(1, Math.max(0, value));
const localProgress = (progress: number, start: number, end: number): number =>
  clamp((progress - start) / Math.max(0.001, end - start));

function resolvePhase(progress: number): FinalePhase {
  if (progress < 0.13) return 'horizon';
  if (progress < 0.38) return 'flagship';
  if (progress < 0.65) return 'fleet';
  return 'ecosystem';
}

function resolveVideo(progress: number): number {
  if (progress < 0.38) return 0;
  if (progress < 0.65) return 1;
  return 2;
}

function createVideoScrubber(video: HTMLVideoElement): VideoScrubber {
  let duration = 0;
  let progress = 0;
  let desiredTime = 0;
  let loaded = false;
  let active = false;
  let animationFrame = 0;

  video.muted = true;
  video.playsInline = true;
  video.pause();

  const syncMetadata = (): void => {
    duration = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.05) : 0;
    desiredTime = duration * progress;
    video.pause();
    if (duration > 0 && video.currentTime === 0) video.currentTime = 0.001;
  };

  video.addEventListener('loadedmetadata', syncMetadata);
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) syncMetadata();

  const render = (): void => {
    if (active && duration > 0 && !video.seeking && Math.abs(video.currentTime - desiredTime) > 1 / 48) {
      video.currentTime = desiredTime;
    }
    animationFrame = requestAnimationFrame(render);
  };

  animationFrame = requestAnimationFrame(render);
  document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); });
  window.addEventListener('pagehide', () => cancelAnimationFrame(animationFrame), { once: true });

  return {
    load(): void {
      active = true;
      if (loaded) return;
      loaded = true;
      video.preload = 'auto';
      video.load();
    },
    setProgress(nextProgress: number): void {
      active = true;
      progress = clamp(nextProgress);
      if (duration > 0) desiredTime = duration * progress;
    },
    pause(): void { active = false; video.pause(); },
  };
}

function setProgressState(section: HTMLElement, phase: FinalePhase): void {
  section.querySelectorAll<HTMLElement>('[data-finale-step]').forEach((step) => {
    step.classList.toggle('is-active', step.dataset.finaleStep === phase);
  });
}

function preloadNearSection(section: HTMLElement, scrubbers: VideoScrubber[]): void {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    scrubbers[0]?.load();
    scrubbers[1]?.load();
    observer.disconnect();
  }, { rootMargin: '180% 0px 180% 0px' });
  observer.observe(section);
}

export function initMoveBeyondLimits({ reducedMotion }: FinaleOptions): void {
  const section = document.querySelector<HTMLElement>('#move-beyond-limits');
  const stage = document.querySelector<HTMLElement>('#finale-stage');
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.finale-video'));
  const entry = document.querySelector<HTMLElement>('#finale-entry');
  const entryLine = document.querySelector<HTMLElement>('#finale-entry i');
  const entryPoint = document.querySelector<HTMLElement>('#finale-entry span');
  const opening = document.querySelector<HTMLElement>('#finale-opening');
  const openingEyebrow = document.querySelector<HTMLElement>('#finale-opening > p:first-child');
  const openingLine = document.querySelector<HTMLElement>('.finale-opening-mask > span');
  const openingCopy = document.querySelector<HTMLElement>('#finale-opening > p:last-child');
  const telemetry = document.querySelector<HTMLElement>('#finale-telemetry');
  const message = document.querySelector<HTMLElement>('#finale-message');
  const messageEyebrow = document.querySelector<HTMLElement>('#finale-message > p:first-child');
  const titleLines = Array.from(document.querySelectorAll<HTMLElement>('.finale-title-mask > span'));
  const messageCopy = document.querySelector<HTMLElement>('.finale-copy');
  const actions = document.querySelector<HTMLElement>('.finale-actions');
  const systemLine = document.querySelector<HTMLElement>('#finale-system-line');
  const footer = document.querySelector<HTMLElement>('#finale-footer');
  const progress = document.querySelector<HTMLElement>('#finale-progress');
  const wipe = document.querySelector<HTMLElement>('#finale-structural-wipe');
  const blackout = document.querySelector<HTMLElement>('#finale-opening-blackout');
  const atmosphere = document.querySelector<HTMLElement>('#finale-atmosphere');
  const vignette = document.querySelector<HTMLElement>('#finale-vignette');

  if (
    !section || !stage || videos.length !== 3 || !entry || !entryLine || !entryPoint ||
    !opening || !openingEyebrow || !openingLine || !openingCopy || !telemetry || !message ||
    !messageEyebrow || titleLines.length !== 2 || !messageCopy || !actions || !systemLine ||
    !footer || !progress || !wipe || !blackout || !atmosphere || !vignette
  ) return;

  if (reducedMotion) {
    stage.style.visibility = 'visible';
    section.dataset.finalePhase = 'ecosystem';
    section.dataset.activeVideo = 'static';
    section.dataset.storyProgress = '1.0000';
    return;
  }

  const scrubbers = videos.map(createVideoScrubber);
  preloadNearSection(section, scrubbers);
  const isMobile = window.matchMedia('(max-width: 760px)').matches;
  let loadTier = 1;

  gsap.set(stage, { visibility: 'hidden' });
  gsap.set(videos, { opacity: 0, scale: 1.015, filter: 'brightness(.22) saturate(.72)' });
  gsap.set(videos[0], { opacity: 1 });
  gsap.set(blackout, { opacity: 1 });
  gsap.set(entry, { opacity: 1 });
  gsap.set(entryLine, { width: 0 });
  gsap.set(entryPoint, { scale: 1 });
  gsap.set([opening, telemetry, message, systemLine, footer, progress, wipe], { opacity: 0 });
  gsap.set(openingEyebrow, { opacity: 0, y: 8, letterSpacing: '.5em' });
  gsap.set(openingLine, { yPercent: 116 });
  gsap.set(openingCopy, { opacity: 0, y: 10, filter: 'blur(5px)' });
  gsap.set(messageEyebrow, { opacity: 0, y: 8, letterSpacing: '.48em' });
  gsap.set(titleLines, { yPercent: 116 });
  gsap.set([messageCopy, actions], { opacity: 0, y: 11, filter: 'blur(4px)' });
  gsap.set(wipe, { xPercent: -50 });

  const story = { progress: 0 };
  const syncStory = (): void => {
    const value = clamp(story.progress);
    const phase = resolvePhase(value);
    const activeVideo = resolveVideo(value);

    scrubbers[0]?.setProgress(localProgress(value, 0, 0.38));
    scrubbers[1]?.setProgress(localProgress(value, 0.38, 0.65));
    scrubbers[2]?.setProgress(localProgress(value, 0.65, 0.995));

    if (value >= 0.25 && loadTier < 2) { loadTier = 2; scrubbers[1]?.load(); }
    if (value >= 0.52 && loadTier < 3) { loadTier = 3; scrubbers[2]?.load(); }

    section.dataset.storyProgress = value.toFixed(4);
    section.dataset.activeVideo = String(activeVideo);
    if (section.dataset.finalePhase !== phase) {
      section.dataset.finalePhase = phase;
      setProgressState(section, phase);
    }
  };

  const pauseAll = (): void => scrubbers.forEach((scrubber) => scrubber.pause());

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (isMobile ? 2.9 : 3.2))}`,
      scrub: isMobile ? 0.28 : 0.48,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => { stage.style.visibility = 'visible'; scrubbers[0]?.load(); },
      onEnterBack: () => { stage.style.visibility = 'visible'; scrubbers[2]?.load(); },
      onLeave: () => { story.progress = 1; syncStory(); pauseAll(); },
      onLeaveBack: () => { stage.style.visibility = 'hidden'; story.progress = 0; syncStory(); pauseAll(); },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) stage.style.visibility = 'visible';
      },
    },
  });

  timeline
    .to(story, { progress: 1, duration: 1, ease: 'none', onUpdate: syncStory }, 0)

    // Section 07's final operational point expands into the dawn horizon.
    .to(entryPoint, { left: '50%', scale: 1.7, duration: 0.035, ease: 'power2.inOut' }, 0)
    .to(entryLine, { width: '76vw', duration: 0.055, ease: 'power3.out' }, 0.025)
    .to(blackout, { opacity: 0, duration: 0.075, ease: 'none' }, 0.035)
    .to(videos[0], { filter: 'brightness(.64) saturate(.78)', scale: 1, duration: 0.13, ease: 'none' }, 0.035)
    .to(atmosphere, { opacity: 0.3, duration: 0.1 }, 0.055)
    .to(entry, { opacity: 0, duration: 0.04 }, 0.09)

    // The flagship returns, visually echoing the opening hero without repeating it.
    .to(opening, { opacity: 1, duration: 0.03 }, 0.095)
    .to(openingEyebrow, { opacity: 1, y: 0, letterSpacing: '.32em', duration: 0.045 }, 0.1)
    .to(openingLine, { yPercent: 0, duration: 0.065, ease: 'power3.out' }, 0.105)
    .to(openingCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.045 }, 0.13)
    .to(progress, { opacity: 1, duration: 0.035 }, 0.14)
    .to(telemetry, { opacity: 1, duration: 0.045 }, 0.19)
    .to(videos[0], { filter: 'brightness(.91) saturate(.84)', scale: 1.008, duration: 0.2, ease: 'none' }, 0.16)
    .to(atmosphere, { opacity: 0.44, x: '1vw', duration: 0.18, ease: 'none' }, 0.16)

    // A foreground terminal obstruction conceals the edit as the camera rises to reveal the fleet.
    .to(wipe, { opacity: 1, xPercent: 450, duration: 0.05, ease: 'power2.inOut' }, 0.35)
    .set(videos[0], { opacity: 0 }, 0.38)
    .set(videos[1], { opacity: 1, scale: 1.012, filter: 'brightness(.78) saturate(.82)' }, 0.38)
    .to(wipe, { opacity: 0, duration: 0.02 }, 0.385)
    .to(opening, { opacity: 0, y: -13, duration: 0.04 }, 0.39)
    .to(videos[1], { scale: 1, filter: 'brightness(.91) saturate(.85)', duration: 0.24, ease: 'none' }, 0.39)
    .to(telemetry, { y: -7, duration: 0.18, ease: 'none' }, 0.42)

    // The partial fleet opens into one integrated continental logistics ecosystem.
    .to(wipe, { opacity: 1, xPercent: -50, duration: 0 }, 0.62)
    .to(wipe, { xPercent: 450, duration: 0.05 }, 0.62)
    .set(videos[1], { opacity: 0 }, 0.65)
    .set(videos[2], { opacity: 1, scale: 1.01, filter: 'brightness(.76) saturate(.82)' }, 0.65)
    .to(wipe, { opacity: 0, duration: 0.02 }, 0.655)
    .to(telemetry, { opacity: 0, duration: 0.03 }, 0.65)
    .to(videos[2], { scale: 1, filter: 'brightness(.94) saturate(.88)', duration: 0.32, ease: 'none' }, 0.65)
    .to(vignette, { opacity: 0.72, duration: 0.1 }, 0.65)
    .to(message, { opacity: 1, duration: 0.035 }, 0.69)
    .to(messageEyebrow, { opacity: 1, y: 0, letterSpacing: '.32em', duration: 0.045 }, 0.695)
    .to(titleLines, { yPercent: 0, duration: 0.065, stagger: 0.012, ease: 'power3.out' }, 0.7)
    .to(messageCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.045 }, 0.74)
    .to(actions, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.045 }, 0.765)
    .to(progress, { opacity: 0, duration: 0.03 }, 0.72)
    .to(systemLine, { opacity: 1, duration: 0.055 }, 0.8)
    .to(footer, { opacity: 1, duration: 0.055 }, 0.87)
    .to(atmosphere, { opacity: 0.22, x: '-1vw', duration: 0.22, ease: 'none' }, 0.76);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
